import React, { useEffect, useLayoutEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import NavBar from "../navigation/NavBar";
import SideBar from "../navigation/SideBar";
import Footer from "../navigation/Footer";
import WebsitePage from "./WebsitePage";
import NotFoundWebsite from "./NotFoundWebsite";
import ErrorWebsite from "./ErrorWebsite";
import LoadingPage from "../utils/LoadingPage";
import LightModeWrapper from "../utils/LightModeWrapper";
import LightRevealOverlay from "../animations/LightRevealOverlay";
import {
  getEntryMotionProps,
  getPageTransitionMotionProps,
  isLightReveal,
} from "../animations/websiteAnimations";
import {
  getBreakpoint,
  getSidebarWidth,
  DEFAULT_SIDEBAR_CLASSES,
} from "../../core/utils/pageUtils";
import { apiFetch } from "../../core/utils/api";
import { EditContext } from "../../editor/context/EditContext";
import { usePreviewOptional } from "../../editor/context/PreviewContext";
import { useEditorInteractionOptional } from "../../editor/context/EditorInteractionContext";

const INITIAL_CONTENT_SETTLE_MS = 150;
const DOM_STABLE_MS = 500;
const READY_MAX_WAIT_MS = 12000;
const WEBSITE_CONTENT_QUERY_ROOTS = new Set(["page-skeleton", "component", "item"]);

/**
 * Resolves once no new child-level DOM mutations occur within `stableMs`
 * inside `container`, or after `maxWaitMs` as a safety cap.
 * Only watches structural changes (childList) — attribute and style
 * changes from animations / hover effects are ignored.
 */
function waitForDomStability(container, stableMs = DOM_STABLE_MS, maxWaitMs = READY_MAX_WAIT_MS) {
  return new Promise((resolve) => {
    let settleTimer = null;

    const done = () => {
      observer.disconnect();
      clearTimeout(maxTimer);
      if (settleTimer) clearTimeout(settleTimer);
      resolve();
    };

    const resetSettle = () => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(done, stableMs);
    };

    const observer = new MutationObserver(resetSettle);
    observer.observe(container, { childList: true, subtree: true });

    const maxTimer = setTimeout(done, maxWaitMs);

    resetSettle();
  });
}

function waitForAllImages(container, maxWaitMs = READY_MAX_WAIT_MS) {
  return new Promise((resolve) => {
    const images = Array.from(container.querySelectorAll("img"));
    const pending = images.filter((img) => !img.complete);

    if (pending.length === 0) {
      resolve();
      return;
    }

    let remaining = pending.length;
    const onDone = () => {
      remaining -= 1;
      if (remaining <= 0) resolve();
    };

    pending.forEach((img) => {
      img.addEventListener("load", onDone, { once: true });
      img.addEventListener("error", onDone, { once: true });
    });

    setTimeout(resolve, maxWaitMs);
  });
}

const Website = ({ version_slug, username: usernameProp, domain: domainProp, subdomain: subdomainProp, onReady }) => {
  const { username: routeUsername, domain: routeDomain, subdomain: routeSubdomain } = useParams();
  const username = usernameProp ?? routeUsername;
  const domain = domainProp ?? routeDomain;
  const pageKey = (subdomainProp ?? routeSubdomain ?? "") || "";
  const isEditor = Boolean(version_slug);
  const [notFound, setNotFound] = useState(false);
  const [website, setWebsite] = useState(null);
  const [navbar, setNavbar] = useState(null);
  const [error, setError] = useState("");
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [leftSidebarSticky, setLeftSidebarSticky] = useState(false);
  const [rightSidebarSticky, setRightSidebarSticky] = useState(false);
  const [leftStickyWidthPx, setLeftStickyWidthPx] = useState(null);
  const [rightStickyWidthPx, setRightStickyWidthPx] = useState(null);
  const [initialContentReady, setInitialContentReady] = useState(false);

  const websiteContainerRef = useRef(null);

  // Ref + state to measure navbar height so mobile sidebar overlay starts below it
  const navbarWrapperRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(0);

  // Animation preview (editor) + public entry/page transitions
  const entryControls = useAnimationControls();
  const pageControls = useAnimationControls();
  const [previewRequest, setPreviewRequest] = useState(null);
  const [lightRevealActive, setLightRevealActive] = useState(false);
  const [lightRevealDurationSec, setLightRevealDurationSec] = useState(2.0);
  
  // Get containerWidth from PreviewContext (edit mode) or use window width (public routes)
  const { containerWidth: contextWidth } = usePreviewOptional() || {};
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  useEffect(() => {
    // Only set up window resize listener if we don't have context width
    if (contextWidth !== undefined) return;
    if (typeof window === 'undefined') return;
    
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize(); // Get initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [contextWidth]);
  
  const containerWidth = contextWidth ?? windowWidth;
  
  // Breakpoint is derived from live preview width (editor) or window (public)
  const [breakpoint, setBreakpoint] = useState(getBreakpoint(containerWidth));
  const editContext = useContext(EditContext) || {};
  const editorInteraction = useEditorInteractionOptional();

  useEffect(() => {
    const fetchWebsite = async () => {
      try {
        // Use version_slug if provided (edit mode), otherwise use regular endpoint
        const endpoint = version_slug 
          ? `/api/websites/domain/${username}/${domain}/${version_slug}/`
          : `/api/websites/domain/${username}/${domain}/`;
        
        const res = await apiFetch(endpoint);
        if (!res.ok) {
          if (res.status === 404) {
            setNotFound(true);
            return;
          }
          const data = await res.json();
          setError(data.error || "Website not found");
          return;
        }
        const data = await res.json();
        // On public routes (no version_slug), only show published websites
        if (!version_slug && data && data.is_published === false) {
          setNotFound(true);
          return;
        }
        setWebsite(data);
      } catch {
        setError("Network error");
      }
    };
    fetchWebsite();
  }, [domain, username, version_slug]);

  // Editor AI refresh: navigation changes can create/remove sidebars/footer/navbar.
  // Website-level payload drives whether sidebars are mounted at all (hasLeftSidebar/hasRightSidebar),
  // so we must refetch the website data on navigation refresh events.
  useEffect(() => {
    const fetchWebsite = async () => {
      try {
        const endpoint = version_slug
          ? `/api/websites/domain/${username}/${domain}/${version_slug}/`
          : `/api/websites/domain/${username}/${domain}/`;

        const res = await apiFetch(endpoint);
        if (!res.ok) return;
        const data = await res.json();
        setWebsite(data);
      } catch {
        // Non-fatal: UI can continue showing the previous website state.
      }
    };

    const handler = (evt) => {
      const detail = evt?.detail || {};
      // Only react in edit-mode contexts where we have a website loaded.
      if (!website?.id) return;
      if (!detail?.websiteId) return;
      if (String(detail.websiteId) !== String(website.id)) return;

      // Navigation changes should refresh website payload so sidebars appear/disappear immediately.
      if (detail.reason === "navigation" || detail.reason === "website-animations") {
        const run = () => fetchWebsite();
        if (typeof editorInteraction?.runWhenIdle === "function") {
          editorInteraction.runWhenIdle(run);
        } else {
          run();
        }
      }
    };

    window.addEventListener("editor:refresh-website", handler);
    return () => window.removeEventListener("editor:refresh-website", handler);
  }, [website?.id, username, domain, version_slug, editorInteraction]);

  const triggerLightReveal = useCallback((durationSec) => {
    const n = typeof durationSec === "number" ? durationSec : parseFloat(String(durationSec));
    setLightRevealDurationSec(Number.isFinite(n) ? n : 2.0);
    setLightRevealActive(true);
  }, []);

  // Editor preview: listen for WebsiteAnimationsPanel preview events and replay animations without unmounting.
  useEffect(() => {
    if (!isEditor) return;

    const handler = (evt) => {
      const detail = evt?.detail || {};
      if (!website?.id) return;
      if (!detail?.websiteId) return;
      if (String(detail.websiteId) !== String(website.id)) return;

      setPreviewRequest({
        ts: detail?.ts || Date.now(),
        trigger: detail?.trigger || "both",
        animation: detail?.animation || null,
      });
    };

    window.addEventListener("editor:website-animations:preview", handler);
    return () => window.removeEventListener("editor:website-animations:preview", handler);
  }, [isEditor, website?.id]);

  // Extract specific values we care about to avoid depending on full objects
  const selectedElementType = editContext?.selectedElement?.type;
  const saveStatus = editContext?.saveStatus;
  
  // Navbar fetch helper:
  // - Do NOT clear navbar on transient errors (prevents navbar "disappearing" during unrelated edits).
  // - Only set navbar null on 404 (explicitly missing).
  const websiteIdForNav = website?.id;
  const contentFetchingCount = useIsFetching({
    predicate: (query) => {
      const queryKeyRoot = query?.queryKey?.[0];
      if (!WEBSITE_CONTENT_QUERY_ROOTS.has(queryKeyRoot)) return false;
      if (queryKeyRoot !== "page-skeleton") return true;

      return (
        query?.queryKey?.[1] === websiteIdForNav &&
        query?.queryKey?.[2] === (pageKey || "main")
      );
    },
  });

  useEffect(() => {
    setInitialContentReady(false);
  }, [websiteIdForNav, pageKey]);

  useEffect(() => {
    if (isEditor) {
      setInitialContentReady(true);
      return;
    }
    if (!websiteIdForNav || initialContentReady || contentFetchingCount > 0) return;

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      if (cancelled) return;

      const container = websiteContainerRef.current;
      if (container) {
        await waitForDomStability(container);
        if (cancelled) return;
        await waitForAllImages(container);
      }

      if (!cancelled) {
        setInitialContentReady(true);
      }
    }, INITIAL_CONTENT_SETTLE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [contentFetchingCount, initialContentReady, isEditor, websiteIdForNav]);

  useEffect(() => {
    if (initialContentReady && onReady) onReady();
  }, [initialContentReady, onReady]);

  const fetchNavbar = useCallback(async () => {
    if (!websiteIdForNav) return;
    try {
      const res = await apiFetch(`/api/websites/${websiteIdForNav}/navbar/`);
      if (res.ok) {
        const data = await res.json();
        setNavbar(data);
        return;
      }
      if (res.status === 404) {
        setNavbar(null);
        return;
      }
      // Non-fatal: keep current navbar state on other errors.
    } catch {
      // Non-fatal: keep current navbar state on network errors.
    }
  }, [websiteIdForNav]);

  // Initial and website-change navbar fetch
  useEffect(() => {
    fetchNavbar();
  }, [fetchNavbar]);

  // Refetch after saving navbar or its sections to persist latest values (avoid refetching on unrelated edits)
  useEffect(() => {
    if (!websiteIdForNav) return;
    if (saveStatus !== "saved") return;
    if (selectedElementType !== "navbar" && selectedElementType !== "navbar_section") return;
    fetchNavbar();
  }, [websiteIdForNav, selectedElementType, saveStatus, fetchNavbar]);

  // Targeted refresh: navbar updates should NOT trigger a full website refetch.
  // EditContext emits `editor:refresh-navbar` on autosave so the preview stays correct even after deselecting.
  useEffect(() => {
    const handler = (evt) => {
      const detail = evt?.detail || {};
      if (!websiteIdForNav) return;
      if (!detail?.websiteId) return;
      if (String(detail.websiteId) !== String(websiteIdForNav)) return;
      fetchNavbar();
    };

    window.addEventListener("editor:refresh-navbar", handler);
    return () => window.removeEventListener("editor:refresh-navbar", handler);
  }, [websiteIdForNav, fetchNavbar]);

  // Targeted refresh: footer updates should not require a full website refetch.
  // EditContext emits `editor:refresh-footer` on autosave.
  useEffect(() => {
    const fetchFooter = async () => {
      if (!website?.id) return;
      try {
        const res = await apiFetch(`/api/websites/${website.id}/footer/`);
        if (res.ok) {
          const data = await res.json();
          setWebsite((prev) => (prev ? { ...prev, footer: data } : prev));
        } else if (res.status === 404) {
          setWebsite((prev) => (prev ? { ...prev, footer: null } : prev));
        }
      } catch {
        // Non-fatal: keep the current footer state.
      }
    };

    const handler = (evt) => {
      const detail = evt?.detail || {};
      if (!website?.id) return;
      if (!detail?.websiteId) return;
      if (String(detail.websiteId) !== String(website.id)) return;
      fetchFooter();
    };

    window.addEventListener("editor:refresh-footer", handler);
    return () => window.removeEventListener("editor:refresh-footer", handler);
  }, [website?.id]);

  // Keep breakpoint in sync with containerWidth (editor preview case)
  // Only update if the breakpoint value actually changes to prevent unnecessary re-renders
  useEffect(() => {
    if (typeof containerWidth === "number" && !Number.isNaN(containerWidth)) {
      const newBreakpoint = getBreakpoint(containerWidth);
      setBreakpoint(prevBreakpoint => {
        // Only update if the breakpoint category actually changed
        if (prevBreakpoint !== newBreakpoint) {
          return newBreakpoint;
        }
        return prevBreakpoint;
      });
    }
  }, [containerWidth]);

  // Public route case: update breakpoint on window resize when containerWidth is not provided
  useEffect(() => {
    if (typeof containerWidth === "number" && !Number.isNaN(containerWidth)) {
      return; // editor drives updates via containerWidth effect above
    }
    const handleResize = () => setBreakpoint(getBreakpoint());
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [containerWidth]);

  const hasLeftSidebar = website?.left_sidebar !== null && website?.left_sidebar !== undefined;
  const hasRightSidebar = website?.right_sidebar !== null && website?.right_sidebar !== undefined;
  const isMobileSidebarViewport = breakpoint === "extra_small" || breakpoint === "small";

  // Keep navbarHeight in sync via ResizeObserver.
  // Uses useLayoutEffect so the measurement is available before the first paint,
  // preventing the mobile sidebar overlay from briefly rendering at top:0.
  // Always measured (not gated by isMobileSidebarViewport) so the value is ready
  // the moment the viewport switches to mobile.
  useLayoutEffect(() => {
    const el = navbarWrapperRef.current;
    if (!el) {
      setNavbarHeight(0);
      return;
    }
    const update = () => {
      const h = el.offsetHeight || 0;
      setNavbarHeight((prev) => (prev === h ? prev : h));
    };
    update();
    let ro;
    try {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } catch (_) {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    return () => {
      try {
        ro && ro.disconnect();
      } catch (_) {}
    };
  }, [navbar]);

  useEffect(() => {
    setLeftSidebarSticky(Boolean(website?.left_sidebar?.sticky));
    setRightSidebarSticky(Boolean(website?.right_sidebar?.sticky));
  }, [website?.left_sidebar?.sticky, website?.right_sidebar?.sticky]);

  const leftStickyDesktop = hasLeftSidebar && leftSidebarSticky && !isMobileSidebarViewport;
  const rightStickyDesktop = hasRightSidebar && rightSidebarSticky && !isMobileSidebarViewport;
  const shouldKeepBothSidebarsOpen = leftStickyDesktop || rightStickyDesktop;
  const effectiveShowLeftSidebar = leftStickyDesktop || showLeftSidebar;
  const effectiveShowRightSidebar = rightStickyDesktop || showRightSidebar;
  const showLeftSidebarToggle = hasLeftSidebar && !leftStickyDesktop;
  const showRightSidebarToggle = hasRightSidebar && !rightStickyDesktop;
  const leftSidebarShellWidth = Math.max(225, Number(leftStickyWidthPx) || 225);
  const rightSidebarShellWidth = Math.max(225, Number(rightStickyWidthPx) || 225);
  const useMeasuredLeftSidebarWidth = effectiveShowLeftSidebar && !isMobileSidebarViewport;
  const useMeasuredRightSidebarWidth = effectiveShowRightSidebar && !isMobileSidebarViewport;
  const leftSidebarShellStyle = useMeasuredLeftSidebarWidth
    ? {
        width: `${leftSidebarShellWidth}px`,
        minWidth: "225px",
        maxWidth: `${leftSidebarShellWidth}px`,
        flexBasis: `${leftSidebarShellWidth}px`,
      }
    : undefined;
  const rightSidebarShellStyle = useMeasuredRightSidebarWidth
    ? {
        width: `${rightSidebarShellWidth}px`,
        minWidth: "225px",
        maxWidth: `${rightSidebarShellWidth}px`,
        flexBasis: `${rightSidebarShellWidth}px`,
      }
    : undefined;

  useEffect(() => {
    // Keep the previous mutual-exclusion behavior unless a desktop sticky sidebar
    // requires both to stay open.
    if (shouldKeepBothSidebarsOpen) return;
    if (effectiveShowLeftSidebar && effectiveShowRightSidebar) {
      setShowRightSidebar(false);
    }
  }, [effectiveShowLeftSidebar, effectiveShowRightSidebar, shouldKeepBothSidebarsOpen]);

  // Clip sidebar overflow during width transitions so content doesn't spill out,
  // but allow overflow once expanded so box-shadow is visible above adjacent content.
  const SIDEBAR_TRANSITION_MS = 350;
  const [leftSidebarTransitioning, setLeftSidebarTransitioning] = useState(false);
  const [rightSidebarTransitioning, setRightSidebarTransitioning] = useState(false);

  useEffect(() => {
    if (effectiveShowLeftSidebar) {
      setLeftSidebarTransitioning(true);
      const timer = setTimeout(() => setLeftSidebarTransitioning(false), SIDEBAR_TRANSITION_MS);
      return () => clearTimeout(timer);
    } else {
      setLeftSidebarTransitioning(false);
    }
  }, [effectiveShowLeftSidebar]);

  useEffect(() => {
    if (effectiveShowRightSidebar) {
      setRightSidebarTransitioning(true);
      const timer = setTimeout(() => setRightSidebarTransitioning(false), SIDEBAR_TRANSITION_MS);
      return () => clearTimeout(timer);
    } else {
      setRightSidebarTransitioning(false);
    }
  }, [effectiveShowRightSidebar]);

  const leftSidebarClipOverflow = !effectiveShowLeftSidebar || leftSidebarTransitioning;
  const rightSidebarClipOverflow = !effectiveShowRightSidebar || rightSidebarTransitioning;

  const toggleSidebar = (openState, setOpen, setOther, otherIsStickyDesktop) => {
    // Mutual exclusion: opening one sidebar automatically closes the other.
    if (!openState) {
      if (!otherIsStickyDesktop) {
        setOther(false);
      }
    }
    setOpen(!openState);
  };

  const handleToggleLeftSidebar = () => {
    if (leftStickyDesktop) return;
    toggleSidebar(showLeftSidebar, setShowLeftSidebar, setShowRightSidebar, rightStickyDesktop);
  };
  const handleToggleRightSidebar = () => {
    if (rightStickyDesktop) return;
    toggleSidebar(showRightSidebar, setShowRightSidebar, setShowLeftSidebar, leftStickyDesktop);
  };

  const effectiveAnimation = useMemo(() => {
    if (isEditor && previewRequest?.animation) {
      return previewRequest.animation;
    }
    return website?.animation || null;
  }, [isEditor, previewRequest?.animation, website?.animation]);

  const entryTypeRaw = effectiveAnimation?.entry_animation ?? null;
  const pageTransitionTypeRaw = effectiveAnimation?.page_transition_animation ?? null;
  const entryEnabled = Boolean(entryTypeRaw);
  const pageTransitionEnabled = Boolean(pageTransitionTypeRaw);

  const entryMotion = useMemo(
    () =>
      getEntryMotionProps({
        type: entryTypeRaw || "fade",
        duration: effectiveAnimation?.entry_duration,
      }),
    [entryTypeRaw, effectiveAnimation?.entry_duration],
  );
  const pageMotion = useMemo(
    () =>
      getPageTransitionMotionProps({
        type: pageTransitionTypeRaw || "fade",
        duration: effectiveAnimation?.page_transition_duration,
      }),
    [pageTransitionTypeRaw, effectiveAnimation?.page_transition_duration],
  );

  // Keep editor preview wrappers visible by default (no automatic animation on mount).
  useEffect(() => {
    if (!isEditor) return;
    try {
      entryControls.set("animate");
    } catch (_) {}
    try {
      pageControls.set("animate");
    } catch (_) {}
  }, [isEditor, website?.id, entryTypeRaw, pageTransitionTypeRaw, entryControls, pageControls]);

  // Replay on preview requests (Roadmap 2 -> Roadmap 3 contract)
  useEffect(() => {
    if (!isEditor) return;
    if (!previewRequest?.ts) return;

    const trigger = previewRequest?.trigger || "both";
    const anim = previewRequest?.animation || {};

    if ((trigger === "entry" || trigger === "both") && anim?.entry_animation) {
      if (isLightReveal(anim.entry_animation)) {
        triggerLightReveal(anim?.entry_duration ?? 2.0);
      }
      try {
        entryControls.set("initial");
        entryControls.start("animate");
      } catch (_) {}
    }

    if (
      (trigger === "page_transition" || trigger === "both") &&
      anim?.page_transition_animation
    ) {
      if (isLightReveal(anim.page_transition_animation)) {
        triggerLightReveal(anim?.page_transition_duration ?? 1.0);
      }
      try {
        pageControls.set("initial");
        pageControls.start("animate");
      } catch (_) {}
    }
  }, [isEditor, previewRequest?.ts, previewRequest, entryControls, pageControls, triggerLightReveal]);

  // Public: light_reveal overlays on entry and on page navigation (when configured).
  useEffect(() => {
    if (isEditor) return;
    if (!website?.id) return;
    if (!entryEnabled) return;
    if (!isLightReveal(entryTypeRaw)) return;
    triggerLightReveal(effectiveAnimation?.entry_duration ?? 2.0);
  }, [isEditor, website?.id, entryEnabled, entryTypeRaw, effectiveAnimation?.entry_duration, triggerLightReveal]);

  useEffect(() => {
    if (isEditor) return;
    if (!website?.id) return;
    if (!pageTransitionEnabled) return;
    if (!isLightReveal(pageTransitionTypeRaw)) return;
    triggerLightReveal(effectiveAnimation?.page_transition_duration ?? 1.0);
  }, [
    isEditor,
    website?.id,
    pageKey,
    pageTransitionEnabled,
    pageTransitionTypeRaw,
    effectiveAnimation?.page_transition_duration,
    triggerLightReveal,
  ]);

  // Mobile overlay: whether any sidebar is open in mobile viewport
  // (computed before early returns so the scroll-lock effect can reference it)
  const mobileSidebarOpen = isMobileSidebarViewport && (effectiveShowLeftSidebar || effectiveShowRightSidebar);

  // Lock body scroll when a mobile sidebar overlay is open so only the sidebar drawer scrolls.
  // In the editor the phone frame viewport manages its own scroll (overflow-y:auto on
  // .device-viewport), so we must NOT touch document.body — doing so would lock the
  // entire editor UI instead of just the phone content.
  useEffect(() => {
    if (isEditor) return;
    if (mobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileSidebarOpen, isEditor]);

  if (notFound) return <NotFoundWebsite username={username} domain={domain} />;
  if (error) return <ErrorWebsite username={username} domain={domain} error={error} />;
  if (!website) return <LoadingPage />;

  const websiteShell = (
    <>
      {/* NavBar wrapper: on mobile, sits above the sidebar overlay (z-60) */}
      <div
        ref={navbarWrapperRef}
        className={`${navbar?.sticky_on_scroll !== false ? "sticky top-0" : "relative"} ${
          isMobileSidebarViewport ? "z-[60]" : "z-30"
        }`}
      >
        <NavBar
          navbar={navbar}
          username={username}
          domain={domain}
          toggleLeftSidebar={showLeftSidebarToggle ? handleToggleLeftSidebar : null}
          toggleRightSidebar={showRightSidebarToggle ? handleToggleRightSidebar : null}
        />
      </div>

      {/* ── Mobile: dark backdrop overlay when a sidebar is open (starts below navbar) ── */}
      {isMobileSidebarViewport && (
        <div
          className={`fixed left-0 right-0 bottom-0 z-40 bg-black/50 transition-opacity duration-300 ${
            mobileSidebarOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          style={{ top: `${navbarHeight}px` }}
          onClick={() => {
            setShowLeftSidebar(false);
            setShowRightSidebar(false);
          }}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile: left sidebar as fixed overlay drawer (starts below navbar) ── */}
      {isMobileSidebarViewport && hasLeftSidebar && (
        <div
          data-testid="website-sidebar-shell-left-mobile"
          className={`fixed left-0 bottom-0 z-50 w-[85%] max-w-[420px] bg-white shadow-xl overflow-y-auto transition-transform duration-300 ease-in-out ${
            effectiveShowLeftSidebar ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{ top: `${navbarHeight}px` }}
        >
          <SideBar
            websiteId={website.id}
            username={username}
            domain={domain}
            side="left"
            onStickyChange={setLeftSidebarSticky}
            onStickyWidthChange={setLeftStickyWidthPx}
          />
        </div>
      )}

      {/* ── Mobile: right sidebar as fixed overlay drawer (starts below navbar) ── */}
      {isMobileSidebarViewport && hasRightSidebar && (
        <div
          data-testid="website-sidebar-shell-right-mobile"
          className={`fixed right-0 bottom-0 z-50 w-[85%] max-w-[420px] bg-white shadow-xl overflow-y-auto transition-transform duration-300 ease-in-out ${
            effectiveShowRightSidebar ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ top: `${navbarHeight}px` }}
        >
          <SideBar
            websiteId={website.id}
            username={username}
            domain={domain}
            side="right"
            onStickyChange={setRightSidebarSticky}
            onStickyWidthChange={setRightStickyWidthPx}
          />
        </div>
      )}

      <div className="flex flex-grow">
        {/* ── Desktop: left sidebar as flex child (pushes content) ── */}
        {!isMobileSidebarViewport && (
          <div
            data-testid="website-sidebar-shell-left"
            className={`relative z-10 ${DEFAULT_SIDEBAR_CLASSES} ${leftSidebarClipOverflow ? 'overflow-hidden' : ''} ${getSidebarWidth(
              effectiveShowLeftSidebar,
              breakpoint,
            )}`}
            style={leftSidebarShellStyle}
          >
            {hasLeftSidebar && (
              <SideBar
                websiteId={website.id}
                username={username}
                domain={domain}
                side="left"
                onStickyChange={setLeftSidebarSticky}
                onStickyWidthChange={setLeftStickyWidthPx}
              />
            )}
          </div>
        )}
        <div
          className={`${
            !isMobileSidebarViewport &&
            hasLeftSidebar &&
            effectiveShowLeftSidebar &&
            hasRightSidebar &&
            effectiveShowRightSidebar
              ? "w-1/2"
              : "w-full"
          } m-0 flex flex-col flex-grow min-w-0`}
        >
          <WebsitePage
            websiteId={website.id}
            domain={domain}
            subdomain={subdomainProp}
          />
        </div>
        {/* ── Desktop: right sidebar as flex child (pushes content) ── */}
        {!isMobileSidebarViewport && (
          <div
            data-testid="website-sidebar-shell-right"
            className={`relative z-10 ${DEFAULT_SIDEBAR_CLASSES} ${rightSidebarClipOverflow ? 'overflow-hidden' : ''} ${getSidebarWidth(
              effectiveShowRightSidebar,
              breakpoint,
            )}`}
            style={rightSidebarShellStyle}
          >
            {hasRightSidebar && (
              <SideBar
                websiteId={website.id}
                username={username}
                domain={domain}
                side="right"
                onStickyChange={setRightSidebarSticky}
                onStickyWidthChange={setRightStickyWidthPx}
              />
            )}
          </div>
        )}
      </div>
      {website.footer && (
        <Footer footer={website.footer} containerId={website.container_id} />
      )}
    </>
  );
  const showInitialContentLoader = !isEditor && !initialContentReady;

  return (
    <LightModeWrapper>
      <div ref={websiteContainerRef} className="relative min-h-screen bg-white">
        {showInitialContentLoader && (
          <div className="absolute inset-0 z-[100] bg-white">
            <LoadingPage />
          </div>
        )}

        <div
          className={showInitialContentLoader ? "invisible pointer-events-none" : ""}
          aria-hidden={showInitialContentLoader ? "true" : undefined}
        >
          <LightRevealOverlay
            isActive={lightRevealActive}
            durationSec={lightRevealDurationSec}
            onDone={() => setLightRevealActive(false)}
          />

          <motion.div
            className="flex flex-col min-h-screen bg-white"
            style={{ overflowX: "clip" }}
            initial={isEditor ? false : entryEnabled ? "initial" : false}
            animate={isEditor ? entryControls : entryEnabled ? "animate" : undefined}
            variants={entryMotion?.variants || undefined}
            transition={entryEnabled ? entryMotion?.transition || undefined : { duration: 0 }}
          >
            {isEditor ? (
              <motion.div
                initial={false}
                animate={pageControls}
                variants={pageMotion?.variants || undefined}
                transition={pageMotion?.transition || undefined}
                className="flex flex-col min-h-screen"
              >
                {websiteShell}
              </motion.div>
            ) : pageTransitionEnabled ? (
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={pageKey || "__home__"}
                  variants={pageMotion?.variants || undefined}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageMotion?.transition || undefined}
                  className="flex flex-col min-h-screen"
                >
                  {websiteShell}
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="flex flex-col min-h-screen">{websiteShell}</div>
            )}
          </motion.div>
        </div>
      </div>
    </LightModeWrapper>
  );
};

export default React.memo(Website);
